/**
 * Excel-as-source-of-truth Price Catalog admin page.
 *
 * Single import path: upload .xlsx → entire `price_catalog` is
 * rewritten. Read/write live on the local Mongo only — there is no
 * Supabase mirror to wait on, no Phase D scheduler, no FK cascades.
 *
 * The legacy `AdminPriceCatalog.jsx` (3.3k lines, half of it tied to
 * the Supabase parser stack) is intentionally NOT mounted any more.
 * If you need it back, restore via the platform rollback feature.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, Download, Loader2, Filter, RefreshCw, Trash2, Plus, Pencil, X, Save,
  AlertTriangle, Database, FileSpreadsheet, Search, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───────────────────────── api helpers ───────────────────────── */
const apiHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const apiGet = async (token, path) => {
  const r = await fetch(`${API_URL}${path}`, { headers: apiHeaders(token) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`);
  return r.json();
};
const apiSend = async (token, method, path, body) => {
  const r = await fetch(`${API_URL}${path}`, {
    method, headers: apiHeaders(token),
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || `HTTP ${r.status}`);
  return r.json();
};

/* ───────────────────────── main page ───────────────────────── */
export default function AdminExcelCatalog({ token }) {
  const [stats, setStats]       = useState(null);
  const [options, setOptions]   = useState([]);
  const [filters, setFilters]   = useState({
    vehicle_type: '', brand: '', model: '', engine: '', ecu_type: '',
    option_key: '', search: '',
  });
  const [page, setPage]         = useState(1);
  const [pageSize]              = useState(50);
  const [vehicles, setVehicles] = useState({ total: 0, rows: [] });
  const [loading, setLoading]   = useState(false);

  /* distinct dropdown values — populated from the catalog itself. */
  const [distincts, setDistincts] = useState({
    vehicle_types: [], brands: [], models: [], engines: [], ecu_types: [],
  });

  const loadStats   = useCallback(async () => {
    try { setStats(await apiGet(token, '/api/admin/excel-catalog/stats')); }
    catch (e) { toast.error(`stats: ${e.message}`); }
  }, [token]);
  const loadOptions = useCallback(async () => {
    try { setOptions(await apiGet(token, '/api/admin/excel-catalog/options')); }
    catch (e) { toast.error(`options: ${e.message}`); }
  }, [token]);
  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ ...filters, page, page_size: pageSize });
      const data = await apiGet(token, `/api/admin/excel-catalog/vehicles?${qs}`);
      setVehicles(data);
    } catch (e) { toast.error(`vehicles: ${e.message}`); }
    finally { setLoading(false); }
  }, [token, filters, page, pageSize]);

  /* Cascading distincts — refetch whenever an upstream filter changes. */
  useEffect(() => {
    (async () => {
      try {
        const vt = await fetch(`${API_URL}/api/price-catalog/vehicle-types`).then(r => r.json());
        setDistincts(d => ({ ...d, vehicle_types: vt }));
      } catch (_) { /* fail-open */ }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      if (!filters.vehicle_type) {
        setDistincts(d => ({ ...d, brands: [], models: [], engines: [], ecu_types: [] }));
        return;
      }
      try {
        const br = await fetch(`${API_URL}/api/price-catalog/brands?vehicle_type=${encodeURIComponent(filters.vehicle_type)}`).then(r => r.json());
        setDistincts(d => ({ ...d, brands: br, models: [], engines: [], ecu_types: [] }));
      } catch (_) {}
    })();
  }, [filters.vehicle_type]);
  useEffect(() => {
    (async () => {
      if (!filters.brand || !filters.vehicle_type) return;
      try {
        const md = await fetch(`${API_URL}/api/price-catalog/models?vehicle_type=${encodeURIComponent(filters.vehicle_type)}&brand=${encodeURIComponent(filters.brand)}`).then(r => r.json());
        setDistincts(d => ({ ...d, models: md, engines: [], ecu_types: [] }));
      } catch (_) {}
    })();
  }, [filters.brand, filters.vehicle_type]);
  useEffect(() => {
    (async () => {
      if (!filters.model || !filters.brand || !filters.vehicle_type) return;
      try {
        const en = await fetch(`${API_URL}/api/price-catalog/engines?vehicle_type=${encodeURIComponent(filters.vehicle_type)}&brand=${encodeURIComponent(filters.brand)}&model=${encodeURIComponent(filters.model)}`).then(r => r.json());
        setDistincts(d => ({ ...d, engines: en, ecu_types: [] }));
      } catch (_) {}
    })();
  }, [filters.model, filters.brand, filters.vehicle_type]);
  useEffect(() => {
    (async () => {
      if (!filters.engine || !filters.model || !filters.brand || !filters.vehicle_type) return;
      try {
        const ec = await fetch(`${API_URL}/api/price-catalog/ecu-types?vehicle_type=${encodeURIComponent(filters.vehicle_type)}&brand=${encodeURIComponent(filters.brand)}&model=${encodeURIComponent(filters.model)}&engine=${encodeURIComponent(filters.engine)}`).then(r => r.json());
        setDistincts(d => ({ ...d, ecu_types: ec }));
      } catch (_) {}
    })();
  }, [filters.engine, filters.model, filters.brand, filters.vehicle_type]);

  useEffect(() => { loadStats(); loadOptions(); }, [loadStats, loadOptions]);
  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const optionByKey = useMemo(
    () => Object.fromEntries(options.map(o => [o.option_key, o])),
    [options],
  );

  const resetFilters = () => { setFilters({
    vehicle_type: '', brand: '', model: '', engine: '', ecu_type: '',
    option_key: '', search: '',
  }); setPage(1); };

  return (
    <div className="space-y-5 max-w-[80vw] mx-auto">
      {/* ── Header strip ───────────────────────── */}
      <div className="flex items-start justify-between gap-4 bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-5 shadow-lg" data-testid="excel-catalog-header">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6" /> Price Catalog
          </h1>
          <p className="text-sm text-slate-300 mt-1">
            Excel is the single source of truth. Import a workbook to rewrite the catalog;
            edits made here can be exported back.
          </p>
        </div>
        {stats && (
          <div className="text-right text-[11px] text-slate-300 leading-relaxed" data-testid="excel-catalog-stats">
            <div><strong className="text-white">{stats.vehicles_total?.toLocaleString()}</strong> vehicles</div>
            <div><strong className="text-white">{stats.options_visible}</strong> visible options ({stats.options_custom} custom)</div>
            {stats.last_import && (
              <div className="mt-1 text-slate-400">
                Last import: {new Date(stats.last_import.created_at).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>

      <ImportExportBar token={token} onImported={() => { loadStats(); loadOptions(); loadVehicles(); }} />

      {/* Pre-import safety snapshots. Always visible — even when empty
          — so the admin knows the rollback option exists. */}
      <SnapshotsPanel
        token={token}
        onRestored={() => { loadStats(); loadOptions(); loadVehicles(); }}
      />

      <CustomOptionPanel token={token} options={options} onChanged={loadOptions} />

      {/* ── Filter bar ───────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="excel-catalog-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-indigo-600" />
          <span className="font-semibold text-sm text-slate-800">Filters</span>
          <span className="ml-auto text-xs text-slate-500">{vehicles.total.toLocaleString()} rows match</span>
          <button onClick={resetFilters} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="excel-catalog-reset">Reset</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <Select label="Type"   value={filters.vehicle_type} options={distincts.vehicle_types} onChange={v => { setFilters(f => ({ ...f, vehicle_type: v, brand: '', model: '', engine: '', ecu_type: '' })); setPage(1); }} testid="filter-type" />
          <Select label="Brand"  value={filters.brand}        options={distincts.brands}        onChange={v => { setFilters(f => ({ ...f, brand: v, model: '', engine: '', ecu_type: '' })); setPage(1); }} disabled={!filters.vehicle_type} testid="filter-brand" />
          <Select label="Model"  value={filters.model}        options={distincts.models}        onChange={v => { setFilters(f => ({ ...f, model: v, engine: '', ecu_type: '' })); setPage(1); }} disabled={!filters.brand} testid="filter-model" />
          <Select label="Engine" value={filters.engine}       options={distincts.engines}       onChange={v => { setFilters(f => ({ ...f, engine: v, ecu_type: '' })); setPage(1); }} disabled={!filters.model} testid="filter-engine" />
          <Select label="ECU"    value={filters.ecu_type}     options={distincts.ecu_types}     onChange={v => { setFilters(f => ({ ...f, ecu_type: v })); setPage(1); }} disabled={!filters.engine} testid="filter-ecu" />
          <Select label="Option" value={filters.option_key}   options={options.map(o => ({ value: o.option_key, label: o.option_name }))} onChange={v => { setFilters(f => ({ ...f, option_key: v })); setPage(1); }} testid="filter-option" />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="excel-catalog-search"
              placeholder="Search brand / model / engine / ECU…"
              value={filters.search}
              onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
              className="w-full h-9 pl-8 pr-2 text-sm rounded-lg border border-slate-300 focus:border-indigo-500 outline-none"
            />
          </div>
          {filters.option_key && (
            <DeleteOptionButton
              token={token}
              optionKey={filters.option_key}
              optionLabel={(optionByKey[filters.option_key] || {}).option_name || filters.option_key}
              onDeleted={() => { setFilters(f => ({ ...f, option_key: '' })); loadStats(); loadOptions(); loadVehicles(); }}
            />
          )}
        </div>
      </div>

      {/* ── Vehicles table ───────────────────────── */}
      <VehiclesTable
        token={token}
        vehicles={vehicles}
        options={options}
        optionFilter={filters.option_key}
        loading={loading}
        onChanged={() => { loadVehicles(); loadStats(); }}
      />

      {/* ── Pagination ───────────────────────── */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <button
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
          className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-30"
          data-testid="pager-prev"
        >
          ← Previous
        </button>
        <span>Page {page} / {Math.max(1, Math.ceil(vehicles.total / pageSize))}</span>
        <button
          disabled={page * pageSize >= vehicles.total}
          onClick={() => setPage(p => p + 1)}
          className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-30"
          data-testid="pager-next"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────── helpers / sub-components ───────────────────────── */

function Select({ label, value, options, onChange, disabled, testid }) {
  /* `options` accepts either an array of strings or `{value,label}`. */
  const norm = (options || []).map(o => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <label className="flex flex-col text-[11px] font-semibold text-slate-600">
      {label}
      <select
        data-testid={testid}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 px-2 text-sm rounded-lg border border-slate-300 focus:border-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
      >
        <option value="">All</option>
        {norm.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function ImportExportBar({ token, onImported }) {
  const fileRef = useRef();
  const [busy, setBusy]   = useState(false);
  const [mode, setMode]   = useState('replace');
  const [phase, setPhase] = useState(null);          // 'uploading' | 'processing' | null
  const [percent, setPercent] = useState(0);
  const [report, setReport] = useState(null);        // last import result, opens a modal

  // 08/Jun/2026 safety hardening: before the destructive POST /import
  // we run the file through POST /import/dry-run. The response is held
  // in `dryRun` so the operator can review row counts + warnings in a
  // modal and explicitly Confirm before we touch the database.
  const [dryRun, setDryRun] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const onFileSelected = async (f) => {
    if (!f) return;
    setBusy(true); setPhase('processing'); setPercent(0);
    setPendingFile(f);
    const tid = toast.loading('Validating workbook (dry-run)…');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API_URL}/api/admin/excel-catalog/import/dry-run`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      toast.dismiss(tid);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setDryRun(j);
    } catch (e) {
      toast.error(`Dry-run failed: ${e.message}`);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setBusy(false); setPhase(null); setPercent(0);
    }
  };

  const doImport = (f) => {
    if (!f) return;
    setBusy(true);
    setPhase('uploading');
    setPercent(0);
    setDryRun(null);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/admin/excel-catalog/import?mode=${mode}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setPercent(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.upload.onload = () => {
      setPhase('processing');
      setPercent(99);
    };
    xhr.onerror = () => {
      toast.error('Network error during import.');
      setBusy(false); setPhase(null); setPercent(0);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = '';
    };
    xhr.onload = () => {
      let j = {};
      try { j = JSON.parse(xhr.responseText); } catch (_) { /* keep empty */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        setPercent(100);
        setPhase(null);
        setBusy(false);
        setPendingFile(null);
        if (fileRef.current) fileRef.current.value = '';
        const d = j.diff || {};
        toast.success(
          `Imported ${j.vehicles_total?.toLocaleString() || 0} vehicles · ` +
          `${d.new_vehicles_total || 0} new · ${d.removed_vehicles_total || 0} removed · ` +
          `${d.changed_prices_total || 0} price changes` +
          (j.snapshot_id ? ` · safety snapshot saved` : ''),
          { duration: 10000 },
        );
        setReport(j);
        onImported?.();
      } else {
        toast.error(`Import failed: ${j?.detail || `HTTP ${xhr.status}`}`);
        setBusy(false); setPhase(null); setPercent(0);
        if (fileRef.current) fileRef.current.value = '';
      }
    };

    const fd = new FormData();
    fd.append('file', f);
    xhr.send(fd);
  };

  const doExport = async () => {
    setBusy(true);
    setPhase('processing');     // server is generating the workbook
    setPercent(0);
    const tid = toast.loading('Generating Excel…');
    try {
      const r = await fetch(`${API_URL}/api/admin/excel-catalog/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Stream the response so we can render real download progress
      // even when the server doesn't send Content-Length.
      const total = Number(r.headers.get('Content-Length')) || 0;
      const reader = r.body.getReader();
      const chunks = [];
      let received = 0;
      // Once the first byte arrives we know the server finished
      // generating the workbook, so flip to "downloading" mode.
      setPhase('uploading');     // re-using same UI label for "transfer in flight"
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total > 0) setPercent(Math.min(99, Math.round((received / total) * 100)));
      }
      const blob = new Blob(chunks, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `price-catalog-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setPercent(100);
      toast.dismiss(tid);
      toast.success(`Catalog exported (${(blob.size / 1024 / 1024).toFixed(2)} MB).`);
    } catch (e) {
      toast.dismiss(tid);
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setBusy(false); setPhase(null); setPercent(0);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="excel-catalog-iobar">
        <div className="flex flex-wrap items-center gap-3">
          <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
          <div className="font-semibold text-sm text-slate-800">Master file</div>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            data-testid="excel-import-mode"
            className="h-9 px-2 text-sm rounded border border-slate-300"
            title="replace = wipe + load (recommended). upsert = merge on top of existing, preserve manual edits."
          >
            <option value="replace">replace (recommended)</option>
            <option value="upsert">upsert (preserve manual edits)</option>
          </select>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={(e) => onFileSelected(e.target.files?.[0])}
            className="hidden"
            data-testid="excel-import-file"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="excel-import-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 px-3 py-1.5 rounded-lg"
          >
            {busy && phase ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import Excel
          </button>
          <button
            onClick={doExport}
            disabled={busy}
            data-testid="excel-export-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 px-3 py-1.5 rounded-lg"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
          {report && !busy && (
            <button
              onClick={() => setReport(report)}
              data-testid="excel-show-report-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-2 py-1 rounded border border-indigo-200 bg-indigo-50/50"
            >
              📋 Show last import report
            </button>
          )}
          <p className="text-[11px] text-slate-500 italic ml-auto max-w-md">
            Imports rewrite ALL prices. Existing orders keep their historical prices —
            they're snapshotted at order creation.
          </p>
        </div>
        {/* Progress bar — only renders while a transfer is in flight. */}
        {busy && phase && (
          <div className="mt-3" data-testid="excel-progress-bar">
            <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
              <span className="font-semibold">
                {phase === 'uploading' && 'Uploading workbook…'}
                {phase === 'processing' && 'Processing on server (parsing + diffing + writing Mongo)…'}
              </span>
              <span className="font-mono">{percent}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-200 ${
                  phase === 'processing'
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-gradient-to-r from-indigo-500 to-emerald-500'
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {report && !busy && (
        <ImportReportModal report={report} onClose={() => setReport(null)} />
      )}
      {/* Dry-run confirmation — shown right after file pick and BEFORE
          the destructive POST /import. Operator reviews row counts,
          warnings, then explicitly clicks Confirm to proceed. */}
      {dryRun && pendingFile && (
        <DryRunConfirmModal
          dryRun={dryRun}
          mode={mode}
          fileName={pendingFile.name}
          onCancel={() => {
            setDryRun(null); setPendingFile(null);
            if (fileRef.current) fileRef.current.value = '';
          }}
          onConfirm={() => {
            const f = pendingFile;
            setDryRun(null);
            doImport(f);
          }}
        />
      )}
    </>
  );
}

/**
 * Post-import report. Shows the high-level counters at the top and a
 * trio of scrollable lists below (new vehicles, removed vehicles,
 * changed prices) so the operator can verify the exact deltas without
 * having to re-export and diff Excel files manually.
 */
function ImportReportModal({ report, onClose }) {
  const d = report?.diff || {};
  const sectionTabs = [
    { id: 'new',     label: `New (${d.new_vehicles_total || 0})`,         data: d.new_vehicles || [] },
    { id: 'changed', label: `Price changes (${d.changed_prices_total || 0})`, data: d.changed_prices || [] },
    { id: 'removed', label: `Removed (${d.removed_vehicles_total || 0})`, data: d.removed_vehicles || [] },
  ];
  const [tab, setTab] = useState('new');
  const active = sectionTabs.find(s => s.id === tab) || sectionTabs[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="excel-import-report-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 bg-gradient-to-r from-indigo-600 to-emerald-600 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold">Import report</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="excel-import-report-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 lg:grid-cols-5 gap-3 bg-slate-50">
          <Stat label="Vehicles total" value={(report.vehicles_total || 0).toLocaleString()} tone="slate" />
          <Stat label="New" value={d.new_vehicles_total || 0} tone="emerald" />
          <Stat label="Removed" value={d.removed_vehicles_total || 0} tone="rose" />
          <Stat label="Price changes" value={d.changed_prices_total || 0} tone="amber" />
          <Stat label="Touched rows" value={d.touched_vehicles || 0} tone="indigo" />
        </div>
        {(report.duplicates_resolved || report.rows_skipped) ? (
          <div className="px-5 py-2 text-[11px] text-slate-600 bg-amber-50 border-y border-amber-200">
            {report.duplicates_resolved ? `${report.duplicates_resolved} duplicate row(s) resolved (last write won). ` : ''}
            {report.rows_skipped ? `${report.rows_skipped} row(s) skipped (missing id fields).` : ''}
          </div>
        ) : null}
        <div className="px-5 pt-3 border-b border-slate-200 flex items-center gap-1">
          {sectionTabs.map(s => (
            <button
              key={s.id}
              onClick={() => setTab(s.id)}
              data-testid={`excel-import-report-tab-${s.id}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg transition ${
                tab === s.id ? 'bg-white text-indigo-700 border-x border-t border-slate-200' : 'text-slate-500 hover:text-slate-800'
              }`}
            >{s.label}</button>
          ))}
        </div>
        <div className="flex-1 overflow-auto px-5 py-3">
          {active.data.length === 0 ? (
            <p className="text-center text-slate-500 text-sm py-8">No entries in this category.</p>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="text-slate-700">
                  <th className="text-left px-2 py-1 font-semibold">Type</th>
                  <th className="text-left px-2 py-1 font-semibold">Brand</th>
                  <th className="text-left px-2 py-1 font-semibold">Model</th>
                  <th className="text-left px-2 py-1 font-semibold">Engine</th>
                  <th className="text-left px-2 py-1 font-semibold">ECU</th>
                  {tab === 'changed' && <>
                    <th className="text-left px-2 py-1 font-semibold">Option</th>
                    <th className="text-right px-2 py-1 font-semibold">Old</th>
                    <th className="text-right px-2 py-1 font-semibold">New</th>
                  </>}
                  {tab === 'new' && <th className="text-right px-2 py-1 font-semibold"># opts</th>}
                </tr>
              </thead>
              <tbody>
                {active.data.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-indigo-50/40">
                    <td className="px-2 py-0.5 text-slate-700">{r.vehicle_type}</td>
                    <td className="px-2 py-0.5 text-slate-900 font-medium">{r.brand}</td>
                    <td className="px-2 py-0.5 text-slate-700">{r.model}</td>
                    <td className="px-2 py-0.5 text-slate-700">{r.engine}</td>
                    <td className="px-2 py-0.5 text-slate-700">{r.ecu_type}</td>
                    {tab === 'changed' && <>
                      <td className="px-2 py-0.5 text-slate-600 font-mono">{r.option_name || r.option_key}</td>
                      <td className="px-2 py-0.5 text-right text-slate-500">{r.old_price ?? '—'}</td>
                      <td className={`px-2 py-0.5 text-right font-semibold ${
                        r.kind === 'added'   ? 'text-emerald-700' :
                        r.kind === 'removed' ? 'text-rose-700' :
                        Number(r.new_price) > Number(r.old_price) ? 'text-amber-700' : 'text-sky-700'
                      }`}>{r.new_price ?? '—'}</td>
                    </>}
                    {tab === 'new' && <td className="px-2 py-0.5 text-right text-slate-600">{r.options_count}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {active.data.length >= (d.capped_at || 500) && (
            <p className="text-[10px] text-slate-500 italic text-center mt-2">
              Preview capped at {d.capped_at} rows — full counts shown at the top.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = {
    slate:   'bg-slate-100 text-slate-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    rose:    'bg-rose-100 text-rose-800',
    amber:   'bg-amber-100 text-amber-800',
    indigo:  'bg-indigo-100 text-indigo-800',
  };
  return (
    <div className={`rounded-lg px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function CustomOptionPanel({ token, options, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const submit = async () => {
    if (!name.trim()) return;
    try {
      await apiSend(token, 'POST', '/api/admin/excel-catalog/option', { option_name: name });
      toast.success(`Added "${name}"`);
      setName(''); setAdding(false); onChanged?.();
    } catch (e) { toast.error(`Add failed: ${e.message}`); }
  };
  const visible = (options || []).filter(o => o.visible !== false);
  const hidden = (options || []).filter(o => o.visible === false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="excel-catalog-options-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-sm text-slate-800">Tuning options</span>
        <span className="text-[11px] text-slate-500">
          {visible.length} visible · {hidden.length} hidden ·{' '}
          {(options || []).filter(o => o.is_custom).length} custom
        </span>
        <button
          onClick={() => setAdding(a => !a)}
          data-testid="excel-catalog-add-option-btn"
          className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-2 py-1 rounded border border-indigo-200 bg-indigo-50/50"
        >
          <Plus className="w-3.5 h-3.5" /> Add custom
        </button>
      </div>
      {adding && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-indigo-50 rounded">
          <input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name (will become an Excel column header)"
            data-testid="excel-catalog-new-option-input"
            className="flex-1 h-9 px-2 text-sm rounded border border-slate-300"
          />
          <button
            onClick={submit}
            data-testid="excel-catalog-new-option-save"
            className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded"
          >Save</button>
          <button
            onClick={() => { setAdding(false); setName(''); }}
            className="text-xs text-slate-500 hover:text-slate-900"
          >Cancel</button>
        </div>
      )}
      <p className="text-[11px] text-slate-500 italic">
        Custom options created here survive Excel re-imports. Add a column with the same
        header to your master to attach per-vehicle prices.
      </p>
    </div>
  );
}

function DeleteOptionButton({ token, optionKey, optionLabel, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!window.confirm(`Permanently delete option "${optionLabel}" (${optionKey}) from the catalog and ALL vehicles?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await apiSend(token, 'DELETE', `/api/admin/excel-catalog/option/${encodeURIComponent(optionKey)}`);
      toast.success(`Deleted "${optionLabel}". ${r.vehicles_touched} vehicle rows updated.`);
      onDeleted?.();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
    finally { setBusy(false); }
  };
  return (
    <button
      onClick={submit}
      disabled={busy}
      data-testid="excel-catalog-delete-option-btn"
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-900 px-2 py-1 rounded border border-rose-200 bg-rose-50/50"
      title="Delete this option everywhere (master + every vehicle row)"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
      Delete option everywhere
    </button>
  );
}

function VehiclesTable({ token, vehicles, options, optionFilter, loading, onChanged }) {
  const [editing, setEditing] = useState(null);  // natural_key being edited

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" />
      </div>
    );
  }
  if (!vehicles.rows.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 shadow-sm">
        No vehicles match the current filters.
      </div>
    );
  }
  const optionByKey = Object.fromEntries(options.map(o => [o.option_key, o]));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="w-full text-sm" data-testid="excel-catalog-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Type</th>
            <th className="text-left px-3 py-2 font-semibold">Brand</th>
            <th className="text-left px-3 py-2 font-semibold">Model</th>
            <th className="text-left px-3 py-2 font-semibold">Engine</th>
            <th className="text-left px-3 py-2 font-semibold">ECU</th>
            <th className="text-left px-3 py-2 font-semibold">
              {optionFilter ? `${optionByKey[optionFilter]?.option_name || optionFilter} (€)` : 'Options'}
            </th>
            <th className="text-right px-3 py-2 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.rows.map((v) => (
            <VehicleRow
              key={v.natural_key}
              token={token}
              vehicle={v}
              options={options}
              optionFilter={optionFilter}
              editing={editing === v.natural_key}
              onEdit={() => setEditing(v.natural_key)}
              onClose={() => setEditing(null)}
              onChanged={() => { onChanged?.(); setEditing(null); }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VehicleRow({ token, vehicle, options, optionFilter, editing, onEdit, onClose, onChanged }) {
  const [draft, setDraft] = useState(vehicle);
  // priceMap: { option_key: '60' } — keyed string state so React keeps
  // each input in sync and Save reads the current value, not whatever
  // happened to be in `draft.options` when the last onBlur fired
  // (the previous version raced the click handler).
  const [priceMap, setPriceMap] = useState({});
  // Preserve `info` per option so editing prices doesn't wipe the
  // description that came in from Excel.
  const [infoMap, setInfoMap] = useState({});

  useEffect(() => {
    setDraft(vehicle);
    const pm = {}, im = {};
    for (const o of (vehicle.options || [])) {
      pm[o.option_key] = o.price != null ? String(o.price) : '';
      im[o.option_key] = o.info || null;
    }
    setPriceMap(pm); setInfoMap(im);
  }, [vehicle]);

  const filteredOption = optionFilter
    ? (vehicle.options || []).find(o => o.option_key === optionFilter)
    : null;

  const save = async () => {
    // Materialise the final options[] from the live priceMap so we
    // capture whatever the operator typed last — even if they hit
    // Save while a price input still has focus.
    const finalOptions = [];
    for (const o of options) {
      if (o.visible === false) continue;
      const raw = priceMap[o.option_key];
      const n = Number(raw);
      if (raw === '' || raw == null || isNaN(n) || n <= 0) continue;
      finalOptions.push({
        option_key: o.option_key,
        price: n,
        info: infoMap[o.option_key] || null,
      });
    }
    try {
      await apiSend(token, 'PATCH', `/api/admin/excel-catalog/vehicle/${encodeURIComponent(vehicle.natural_key)}`, {
        vehicle_type: draft.vehicle_type, brand: draft.brand, model: draft.model,
        engine: draft.engine, ecu_type: draft.ecu_type,
        options: finalOptions,
      });
      toast.success(`Saved (${finalOptions.length} options).`);
      onChanged?.();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete this vehicle row?\n${vehicle.brand} ${vehicle.model} ${vehicle.engine}\n\nThis only removes ONE row — other variants stay.`)) return;
    try {
      await apiSend(token, 'DELETE', `/api/admin/excel-catalog/vehicle/${encodeURIComponent(vehicle.natural_key)}`);
      toast.success('Deleted.');
      onChanged?.();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  if (!editing) {
    return (
      <tr className="border-t border-slate-100 hover:bg-indigo-50/30" data-testid={`vehicle-row-${vehicle.natural_key}`}>
        <td className="px-3 py-1.5 text-slate-700">{vehicle.vehicle_type}</td>
        <td className="px-3 py-1.5 text-slate-900 font-medium">{vehicle.brand}</td>
        <td className="px-3 py-1.5 text-slate-700">{vehicle.model}</td>
        <td className="px-3 py-1.5 text-slate-700">{vehicle.engine}</td>
        <td className="px-3 py-1.5 text-slate-700">{vehicle.ecu_type}</td>
        <td className="px-3 py-1.5 text-slate-700">
          {filteredOption
            ? <span className="font-semibold text-emerald-700">{filteredOption.price}€</span>
            : (
              <span className="text-[11px] text-slate-500">
                {(vehicle.options || []).length} option{(vehicle.options || []).length === 1 ? '' : 's'}
              </span>
            )}
        </td>
        <td className="px-3 py-1.5 text-right">
          <button onClick={onEdit} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-testid={`vehicle-edit-${vehicle.natural_key}`}>
            <Pencil className="w-4 h-4 inline" />
          </button>
          <button onClick={remove} className="text-rose-600 hover:text-rose-900 ml-2" title="Delete" data-testid={`vehicle-delete-${vehicle.natural_key}`}>
            <Trash2 className="w-4 h-4 inline" />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-slate-100 bg-indigo-50/40" data-testid={`vehicle-edit-row-${vehicle.natural_key}`}>
      <td colSpan={7} className="px-3 py-3">
        <div className="grid grid-cols-5 gap-2 mb-3">
          <Input label="Type"   value={draft.vehicle_type} onChange={(v) => setDraft({ ...draft, vehicle_type: v })} />
          <Input label="Brand"  value={draft.brand}        onChange={(v) => setDraft({ ...draft, brand: v })} />
          <Input label="Model"  value={draft.model}        onChange={(v) => setDraft({ ...draft, model: v })} />
          <Input label="Engine" value={draft.engine}       onChange={(v) => setDraft({ ...draft, engine: v })} />
          <Input label="ECU"    value={draft.ecu_type}     onChange={(v) => setDraft({ ...draft, ecu_type: v })} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3 max-h-72 overflow-auto bg-white rounded p-2 border border-slate-200">
          {options.filter(o => o.visible !== false).map(o => (
            <label key={o.option_key} className="flex items-center gap-2 text-[11px]">
              <span className="flex-1 truncate text-slate-700" title={o.option_name}>{o.option_name}</span>
              <input
                type="number"
                step="1"
                min="0"
                value={priceMap[o.option_key] ?? ''}
                onChange={(e) => setPriceMap(pm => ({ ...pm, [o.option_key]: e.target.value }))}
                placeholder="—"
                data-testid={`price-input-${o.option_key}`}
                className="w-16 h-7 px-1 text-right text-xs rounded border border-slate-300"
              />
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">
            <X className="w-4 h-4 inline" /> Cancel
          </button>
          <button onClick={save} className="inline-flex items-center gap-1 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg" data-testid="vehicle-save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </td>
    </tr>
  );
}

function Input({ label, value, onChange }) {
  return (
    <label className="flex flex-col text-[11px] font-semibold text-slate-600">
      {label}
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 px-2 text-sm rounded-lg border border-slate-300 focus:border-indigo-500 outline-none"
      />
    </label>
  );
}



/**
 * DryRunConfirmModal — shown after file pick and BEFORE the
 * destructive import. Mirrors the "are you sure?" pattern used by
 * Stripe Dashboard for irreversible operations.
 */
function DryRunConfirmModal({ dryRun, mode, fileName, onCancel, onConfirm }) {
  const cur = dryRun.current_count || 0;
  const inc = dryRun.incoming_count || 0;
  const delta = dryRun.delta || (inc - cur);
  const deltaCls = delta > 0
    ? 'text-emerald-600'
    : delta < 0
      ? 'text-rose-600'
      : 'text-slate-600';
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="dry-run-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-2xl border border-slate-200">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Upload className="w-5 h-5 text-indigo-600" />
          Confirm import
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          File <span className="font-mono">{fileName}</span> · mode <span className="font-bold">{mode}</span> ·
          parsed successfully ({(dryRun.size_bytes / 1024).toFixed(0)} KB)
        </p>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Current</p>
            <p className="text-xl font-bold text-slate-900 tabular-nums">{cur.toLocaleString()}</p>
          </div>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-cyan-700">Incoming</p>
            <p className="text-xl font-bold text-cyan-900 tabular-nums">{inc.toLocaleString()}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Δ Delta</p>
            <p className={`text-xl font-bold tabular-nums ${deltaCls}`}>{delta > 0 ? '+' : ''}{delta.toLocaleString()}</p>
          </div>
        </div>

        <div className="mt-3 text-xs text-slate-600">
          <span className="font-semibold">Tuning options:</span> {dryRun.options_count || 0} columns
          {dryRun.options && dryRun.options.length > 0 && (
            <span className="block text-[11px] text-slate-500 mt-1 italic">
              {dryRun.options.slice(0, 8).map(o => o.name).join(', ')}{dryRun.options.length > 8 ? '…' : ''}
            </span>
          )}
        </div>

        {dryRun.warnings && dryRun.warnings.length > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="dry-run-warnings">
            <p className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Warnings — review before proceeding
            </p>
            <ul className="mt-1 list-disc list-inside text-[11px] text-amber-800 space-y-0.5">
              {dryRun.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-3 p-2.5 rounded bg-slate-100 border border-slate-200 text-[11px] text-slate-600 flex items-start gap-2">
          <span className="text-slate-500">🛡️</span>
          <span>A full safety snapshot of the current catalog will be saved
            BEFORE the rewrite. If anything goes wrong you can roll back in one
            click from the <b>Snapshots</b> panel below.</span>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            data-testid="dry-run-cancel"
            className="px-3 py-1.5 rounded text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200"
          >Cancel</button>
          <button
            onClick={onConfirm}
            data-testid="dry-run-confirm"
            className="px-4 py-1.5 rounded text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 inline-flex items-center gap-1.5"
          >
            <Upload className="w-4 h-4" />
            Yes, import {inc.toLocaleString()} rows
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * SnapshotsPanel — lists the last 10 pre-import safety snapshots so
 * the admin can roll back to the previous catalog state in one click
 * if a bad import broke something.
 */
export function SnapshotsPanel({ token, onRestored }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/excel-catalog/snapshots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSnapshots(await r.json());
    } catch (e) {
      toast.error(`Could not load snapshots: ${e.message}`);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const restore = async (id, count) => {
    if (!window.confirm(`Restore ${count.toLocaleString()} vehicles from this snapshot? Current catalog will be saved as a new "undo" snapshot before the rollback.`)) return;
    setRestoring(id);
    const tid = toast.loading('Restoring snapshot…');
    try {
      const r = await fetch(`${API_URL}/api/admin/excel-catalog/snapshots/${id}/restore`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.dismiss(tid);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      toast.success(`Restored ${j.doc_count.toLocaleString()} vehicles. Undo snapshot: ${j.undo_snapshot.slice(0, 8)}…`, { duration: 8000 });
      await load();
      onRestored?.();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(`Restore failed: ${e.message}`);
    } finally { setRestoring(null); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="snapshots-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-slate-600" />
          <h3 className="text-sm font-bold text-slate-900">Safety snapshots</h3>
          <span className="text-[11px] text-slate-500">— last {snapshots.length} pre-import / pre-restore states</span>
        </div>
        <button onClick={load} disabled={loading} className="text-[11px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Refresh
        </button>
      </div>
      {snapshots.length === 0 && !loading && (
        <p className="text-[11px] text-slate-500 italic">No snapshots yet — your first Excel import will create one automatically.</p>
      )}
      {snapshots.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-wide text-slate-600 text-left">
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5">Reason</th>
                <th className="px-2 py-1.5">Admin</th>
                <th className="px-2 py-1.5 text-right">Vehicles</th>
                <th className="px-2 py-1.5">Note</th>
                <th className="px-2 py-1.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`snapshot-row-${s.id}`}>
                  <td className="px-2 py-1.5 font-mono text-slate-700 tabular-nums whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${s.reason === 'pre-import' ? 'bg-cyan-50 border-cyan-200 text-cyan-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                      {s.reason}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]" title={s.admin_email}>{s.admin_email}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-slate-900 tabular-nums">{(s.doc_count || 0).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-[11px] text-slate-500 truncate max-w-[200px]" title={s.note}>{s.note || '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      onClick={() => restore(s.id, s.doc_count || 0)}
                      disabled={restoring === s.id}
                      data-testid={`snapshot-restore-${s.id}`}
                      className="text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 px-2 py-0.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      {restoring === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}